import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { useAuth } from '../auth/AuthProvider';
import { groupTenantChoices } from '../components/tenantGroups';

/**
 * Signed in, with SEVERAL places to go and none of them chosen yet.
 *
 * `inferActiveTenant` refuses to guess when more than one is usable, and it is
 * right to: landing somebody in an arbitrary organisation is worse than asking,
 * because they may act on what they see before noticing where they are.
 *
 * **This is not the same as having nowhere to go**, and until this page existed
 * the two were rendered identically — `NoUsableTenantPage` told a person with a
 * dozen organisations that they did not belong to one. That reads as an account
 * fault and sends them to an administrator who has nothing to fix.
 *
 * It is reachable by anybody holding more than one usable membership. The case
 * that made it urgent is a white-label partner administrator, who is a member of
 * every customer company they look after — see
 * `docs/architecture/ajnaview-company-reseller-mapping.md` §7.
 */
export function ChooseTenantPage() {
  const { tenantChoices, setTenant, logout, user } = useAuth();

  /*
   * Only somewhere the session can actually GO.
   *
   * `setTenant` refuses a tenant that is not ACTIVE, so offering a suspended one
   * here would be a button that does nothing — the door-into-a-room-with-no-floor
   * the switcher already avoids. Those are explained by `NoUsableTenantPage`,
   * which is where a person with ONLY suspended organisations still lands.
   */
  const enterable = tenantChoices.filter((c) => c.state === 'ACTIVE');
  const groups = groupTenantChoices(enterable);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 720, width: '100%' }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1">
            Choose where to work
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your account reaches {enterable.length} organisations. Pick one to start — you can
            move between them at any time from the selector in the header.
          </Typography>
        </Stack>

        <Stack spacing={2.5}>
          {groups.map((g) => (
            <Box key={g.parent?.tenantId ?? 'ungrouped'}>
              {g.parent && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <ApartmentOutlinedIcon fontSize="small" color="action" />
                  <Button
                    variant="text"
                    onClick={() => setTenant(g.parent!.tenantId)}
                    sx={{ p: 0, minWidth: 0, fontWeight: 700, textTransform: 'none' }}
                  >
                    {g.parent.tenantName}
                  </Button>
                </Stack>
              )}

              <Stack spacing={0.75} sx={{ pl: g.parent ? 3.5 : 0 }}>
                {g.children.map((c) => (
                  <Stack key={c.tenantId} direction="row" spacing={1} alignItems="center">
                    <AccountTreeOutlinedIcon fontSize="small" color="disabled" />
                    <Button
                      variant="text"
                      onClick={() => setTenant(c.tenantId)}
                      sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                    >
                      {c.tenantName}
                    </Button>
                    {/* Said out loud, because access without a membership is a
                        fact worth knowing: it comes from administering the
                        organisation above, and disappears if that changes. */}
                    {c.derived && <Chip size="small" variant="outlined" label="via the parent" />}
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={logout}>
            Sign out
          </Button>
          {user?.isPlatformAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Platform screens are reachable without choosing one.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
